import { useEffect, useState } from "react";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Badge } from "@/components/ui/badge";
import { Spinner } from "@/components/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatCurrency, formatDateTime } from "@/lib/format";

type CreditNote = components["schemas"]["CreditNoteResponse"];

interface InvoiceCreditNotesTableProps {
  invoiceId?: string;
}

function statusVariant(
  status: string | undefined,
): "default" | "secondary" | "outline" | "destructive" {
  switch (status) {
    case "Issued":
      return "default";
    case "Draft":
      return "secondary";
    default:
      return "outline";
  }
}

export function InvoiceCreditNotesTable({ invoiceId }: InvoiceCreditNotesTableProps) {
  const [creditNotes, setCreditNotes] = useState<CreditNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!invoiceId) return;
    let cancelled = false;

    async function fetchCreditNotes() {
      setLoading(true);
      setError(null);
      const { data, error: apiError } = await api.GET(
        "/admin/invoices/{invoiceId}/credit-notes",
        { params: { path: { invoiceId: invoiceId! } } },
      );
      if (cancelled) return;
      if (apiError) {
        setError("Failed to load credit notes.");
      } else {
        setCreditNotes(data ?? []);
      }
      setLoading(false);
    }

    fetchCreditNotes();
    return () => {
      cancelled = true;
    };
  }, [invoiceId]);

  if (error) {
    return <p className="text-destructive">{error}</p>;
  }

  return (
    <div>
      <div className="mb-4 flex items-center gap-3">
        {loading && <Spinner className="size-5" />}
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Reference</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Reason</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead>Created</TableHead>
              <TableHead>Issued</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {creditNotes.length === 0 && !loading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-muted-foreground h-24 text-center">
                  No credit notes.
                </TableCell>
              </TableRow>
            ) : (
              creditNotes.map((cn) => (
                <TableRow key={cn.id}>
                  <TableCell>
                    <span className="font-mono text-xs">{cn.documentNumber}</span>
                  </TableCell>
                  <TableCell>
                    <Badge variant={statusVariant(cn.status)}>{cn.status}</Badge>
                  </TableCell>
                  <TableCell className="max-w-xs truncate">
                    {cn.reason ?? <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell className="text-right">
                    {formatCurrency(cn.amount, cn.currency)}
                  </TableCell>
                  <TableCell>{formatDateTime(cn.createdAt)}</TableCell>
                  <TableCell>
                    {cn.issuedAt ? (
                      formatDateTime(cn.issuedAt)
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
